import * as React from 'react';
import { useTranslation } from 'next-i18next';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { cn, customImageLoader, getAssetSrc } from '@restworld/utils-common';
import { Button as ButtonV2, Logo } from '@restworld/ui-ds-base';
import Image from 'next/image';
import { useHydrateAtoms } from 'jotai/utils';
import { useRouter } from 'next/router';

import { LayoutWrapper } from '../../layouts';
import OrgStepContainer from './animations/org-step-container';
import {
  activeRestaurantOnboardingStateAtom,
  isManualEntryAtom,
  manualAddressFieldsAtom,
} from '../restaurants/restaurant-onboarding-modal/restaurant-onboarding-atoms';
import {
  activeAddressIdAtom,
  activeRestaurantAtom,
  googleAddresInputValueAtom,
} from '../restaurants/restaurant-onboarding-modal/google-address-input-result';
import { restaurantInformationDataAtom } from '../restaurants/restaurant-onboarding-modal/use-restaurant-onboarding-data-query';
import { binaryPicturesAtom } from '../restaurants/restaurant-onboarding-modal/restaurant-onboarding-image-gallery';
import {
  orgOnboardingStepAtom,
  isOrgOnboardingModeAtom,
  useOrgOnboardingSteps,
  orgOnboardingRestaurantDataAtom,
  orgOnboardingJPDataAtom,
  orgOnboardingContactDetailsAtom,
  orgOnboardingExistingUserFlowAtom,
  orgOnboardingReferralInfoAtom,
  orgOnboardingPartnerInfoAtom,
  JP_LIKE_STEPS,
  ORG_DEFAULT_SUB_STEPS,
  useOrgOnboardingReset,
  useOnboardingPageviewSync,
  useOrgOnboardingUrlSync,
} from './use-organization-onboarding';
import ResumeOnboardingModal from './modals/resume-onboarding-modal';
import { useProspectTracking } from './use-prospect-tracking';
import { useRumSubStepViews } from './use-rum-sub-step-views';
import { DEFAULT_REFERRAL_CREDITS } from '../referral/use-referral-credits';
import { withLaterStepTranslations } from './load-later-step-translations';

import RestaurantCreationStep from './steps/restaurant-creation-step';
import { isDeferredBillingCampaign } from './deferred-billing';

// Lazy loaded step components. Step 1 (the venue search) is a static import above: it is the
// first thing every visitor sees and takes over from the server-rendered step 1, so it must not
// wait for a chunk of its own (RW-235, item 1). The steps after the venue search also wait for
// the namespaces that getStaticProps no longer ships (see load-later-step-translations).
const JobPositionCreationStep = React.lazy(
  withLaterStepTranslations(() => import('./steps/job-position-creation-step'))
);
const UserContactDetailsStep = React.lazy(withLaterStepTranslations(() => import('./steps/user-contact-details-step')));
const ReferralIntroScreen = React.lazy(() => import('./referral-intro-screen'));

// ==================== HEADER ====================

const OrganizationOnboardingHeader: React.FunctionComponent = () => {
  const { t } = useTranslation('restaurant-onboarding');
  const currentStep = useAtomValue(orgOnboardingStepAtom);
  const [activeUI, setActiveUI] = useAtom(activeRestaurantOnboardingStateAtom);
  const { shouldShowHeader, goToPreviousStep, isFirstStep } = useOrgOnboardingSteps();

  // Don't show header during JP creation phases (they have their own header)
  const showInternalHeader = React.useMemo(() => {
    if (!shouldShowHeader) return false;
    if (JP_LIKE_STEPS.includes(currentStep)) return false;
    return true;
  }, [shouldShowHeader, currentStep]);

  // Show back button when:
  // 1. Not on first step of org onboarding OR
  // 2. On restaurant creation step AND in the form view (user can go back to search)
  // The bare venue search is the entry point, so it has no back button.
  const shouldShowBackButton =
    !isFirstStep ||
    (currentStep === 'restaurant_creation' &&
      (activeUI === 'restaurant_onboarding_form' || activeUI === 'restaurant_already_exists'));

  // Handle back button click
  const handleBackClick = React.useCallback(() => {
    if (
      currentStep === 'restaurant_creation' &&
      (activeUI === 'restaurant_onboarding_form' || activeUI === 'restaurant_already_exists')
    ) {
      // Go back to Google Maps search view
      // Setting activeUI triggers useOrgSubStepUrlSync which pushes the URL
      setActiveUI('select_restaurant_from_google');
    } else if (
      currentStep === 'user_contact_details' &&
      (activeUI === 'otp_verification' || activeUI === 'contact_already_exists')
    ) {
      // Go back to contact form from OTP verification
      setActiveUI('restaurant_owner_onboarding_form');
    } else {
      // Navigate to previous org onboarding step
      goToPreviousStep();
    }
  }, [currentStep, activeUI, setActiveUI, goToPreviousStep]);

  if (!showInternalHeader) return null;

  return (
    <div className="w-full px-4 py-3 self-stretch border-b border-neutral-40 inline-flex justify-center fixed top-0 bg-neutral-10 [z-index:99]">
      <div className="w-full flex flex-row items-center justify-between max-w-[640px] px-0 sm:px-4">
        {shouldShowBackButton ? (
          <ButtonV2
            size="sm"
            variant="tertiary"
            className="shadow-none"
            onClick={handleBackClick}
            StartIcon={ChevronLeftIcon}
          >
            {t('back') || 'Back'}
          </ButtonV2>
        ) : (
          <Logo variant="horizontal" title={t('restworld_logo_alt')} className="h-8 w-auto text-purple-700" />
        )}
        <div /> {/* Spacer */}
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const ContentWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('pt-[120px] pb-[120px] min-h-screen bg-neutral-10 grid gap-8 max-w-[640px] mx-auto px-4', className)}
    {...props}
  />
);

/**
 * Main Organization Onboarding component.
 * Orchestrates the multi-step flow for new employer onboarding:
 * 1. Restaurant/venue creation (reuses venue onboarding)
 * 2. Job position creation (reuses JP onboarding form)
 * 3. User contact details (name, email, phone, OTP verification)
 *
 * After user contact details, entities are created in the backend and the user
 * is redirected to /employer/job-positions/onboarding?step=company_and_payment
 * for payment setup via the standard JP onboarding flow.
 *
 * No backend entities are created until the user contact details step is completed.
 * All form data is stored in Jotai atoms until then.
 */
const OrganizationOnboarding: React.FunctionComponent = () => {
  // Seed org-onboarding mode synchronously, during this first render, before
  // any child paints. Without this the atom starts false and the mount effect
  // below flips it post-paint, so copy that branches on org mode (e.g. the
  // venue-search heading) would render its standalone variant for one frame and
  // then swap. Scoped to the org flow — standalone JP/venue flows never call
  // this, so the atom's load-bearing false default is preserved there. The
  // mount effect remains as a backstop for re-mounts in a persisted store.
  useHydrateAtoms([[isOrgOnboardingModeAtom, true]]);

  const router = useRouter();
  // Sync URL query params (?step, ?sub_step) ↔ atoms for browser back/forward
  useOrgOnboardingUrlSync();
  // Fire $pageview on every step/sub-step transition (shallow routing bypasses
  // the global Router.events tracker — see hook docstring).
  useOnboardingPageviewSync();
  // Auto-sync prospect data to backend when onboarding atoms change (funnel drop-off tracking)
  useProspectTracking();
  useRumSubStepViews();

  const setIsOrgMode = useSetAtom(isOrgOnboardingModeAtom);
  const currentStep = useAtomValue(orgOnboardingStepAtom);
  const restaurantData = useAtomValue(orgOnboardingRestaurantDataAtom);
  const jpData = useAtomValue(orgOnboardingJPDataAtom);
  const contactDetails = useAtomValue(orgOnboardingContactDetailsAtom);
  const referralInfo = useAtomValue(orgOnboardingReferralInfoAtom);
  const setReferralInfo = useSetAtom(orgOnboardingReferralInfoAtom);
  const partnerInfo = useAtomValue(orgOnboardingPartnerInfoAtom);
  const setPartnerInfo = useSetAtom(orgOnboardingPartnerInfoAtom);
  const setActiveUI = useSetAtom(activeRestaurantOnboardingStateAtom);
  // Setters for SHARED restaurant onboarding atoms — these aren't owned by
  // resetAll() (which only clears org-prefixed atoms) but hold venue search /
  // form state that must be wiped on "Start Fresh" so the user doesn't see
  // stale data from the previous attempt.
  const setRestaurantFormBinaryPictures = useSetAtom(binaryPicturesAtom);
  const setGoogleInputAddressValue = useSetAtom(googleAddresInputValueAtom);
  const setActiveAddressId = useSetAtom(activeAddressIdAtom);
  const setActiveRestaurantName = useSetAtom(activeRestaurantAtom);
  const setRestaurantInformationData = useSetAtom(restaurantInformationDataAtom);
  const setIsManualEntry = useSetAtom(isManualEntryAtom);
  const setManualAddressFields = useSetAtom(manualAddressFieldsAtom);
  const isExistingUserFlow = useAtomValue(orgOnboardingExistingUserFlowAtom);
  const { resetAll } = useOrgOnboardingReset();
  const { activeSteps } = useOrgOnboardingSteps();

  const [showResumeModal, setShowResumeModal] = React.useState(false);
  // Tracks whether user has dismissed the referral intro in this session
  const [referralIntroDismissed, setReferralIntroDismissed] = React.useState(false);

  // Reactively derive whether to show the intro screen (shared by the org
  // referral ?ref= flow and the B2B partner ?partner= flow).
  // This must be reactive (not mount-only) because both infos are set
  // asynchronously after their API calls resolve in index.tsx.
  // Partner takes priority over org referral when both happen to resolve.
  const hasExistingData = !!(restaurantData || jpData || contactDetails);
  const isCampaignPartner = partnerInfo?.source_type === 'campaign';
  // Deferred billing campaigns (Try for Free): no intro screen, the free days sit above
  // the venue search instead.
  const skipIntroForDeferredBilling = isDeferredBillingCampaign(partnerInfo);
  // Name shown on the intro. Ambassadors and org-to-org referrals render an
  // "invited by X" line; a campaign renders its own copy and may have no name.
  const introName =
    partnerInfo?.partner_name ?? partnerInfo?.campaign_name ?? referralInfo?.referrer_restaurant_name ?? null;
  // Free days come from the backend for partner/campaign codes (per-campaign).
  // Fall back to the pre-PR logic (ambassador 2, org referral 3) when free_days
  // is absent, so an existing ambassador link still shows 2 during any window
  // where this app is deployed before the backend ships free_days. An org
  // referral reads its amount from referral_lookup (credits_invitee) when present.
  const introFreeDays =
    partnerInfo?.free_days ??
    (partnerInfo?.partner_name ? 2 : referralInfo?.credits_invitee ?? DEFAULT_REFERRAL_CREDITS);
  // A campaign renders a dedicated copy and may have no name, so it can trigger
  // the intro on its own; the referral / ambassador variant still needs a name
  // (matches the pre-PR gate, avoids an empty "invited by" line).
  const shouldShowReferralIntro =
    (isCampaignPartner || !!introName) &&
    !skipIntroForDeferredBilling &&
    !hasExistingData &&
    !isExistingUserFlow &&
    !referralIntroDismissed &&
    !showResumeModal;

  // Check if there's existing onboarding data on mount
  // Skip if returning from login redirect (existing user flow) — entities are auto-created
  React.useEffect(() => {
    if (isExistingUserFlow) return;

    const hasData = !!(restaurantData || jpData || contactDetails);
    if (hasData) {
      setShowResumeModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Set org onboarding mode on mount
  // Do NOT reset on unmount — persisted atoms must survive page refresh.
  // resetAll() is called explicitly on flow completion (success step).
  React.useEffect(() => {
    setIsOrgMode(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinueOnboarding = React.useCallback(() => {
    setShowResumeModal(false);
    // Continue with existing data (do nothing, atoms are already populated)
  }, []);

  const handleStartFresh = React.useCallback(() => {
    setShowResumeModal(false);

    const firstStep = activeSteps[0];
    // Shallow-replace path. The first step is always restaurant_creation
    // (never a JP step), so the venue search component mounts cleanly without
    // needing a full reload to drop stale in-memory JP form state.
    const savedReferralInfo = referralInfo;
    const savedPartnerInfo = partnerInfo;
    resetAll();
    // resetAll() flips isOrgMode → false (it's intended for flow-completion).
    // We're still inside org onboarding, so put it back.
    setIsOrgMode(true);
    // Wipe shared restaurant onboarding atoms that resetAll() doesn't own.
    // We don't call useRestaurantOnboardingResetActions().resetState() here
    // because that 250ms-deferred reset would race with the URL replace below.
    setRestaurantFormBinaryPictures([]);
    setGoogleInputAddressValue('');
    setActiveAddressId('');
    setActiveRestaurantName('');
    setRestaurantInformationData({});
    setIsManualEntry(false);
    setManualAddressFields({});
    // Restore referral info so the referral code is still sent in the final payload
    if (savedReferralInfo) setReferralInfo(savedReferralInfo);
    // Restore partner info so the partner intro keeps showing after Start Fresh
    // (the partner_code itself travels via useUTMSource, independent of this).
    if (savedPartnerInfo) setPartnerInfo(savedPartnerInfo);
    // Start Fresh lands on the first step's default sub-step (the Google venue
    // search for restaurant_creation).
    const targetSubStep = ORG_DEFAULT_SUB_STEPS[firstStep];
    router.replace(
      {
        pathname: router.pathname,
        query: { step: firstStep, sub_step: targetSubStep },
      },
      undefined,
      { shallow: true }
    );
  }, [
    activeSteps,
    resetAll,
    setIsOrgMode,
    setRestaurantFormBinaryPictures,
    setGoogleInputAddressValue,
    setActiveAddressId,
    setActiveRestaurantName,
    setRestaurantInformationData,
    setIsManualEntry,
    setManualAddressFields,
    referralInfo,
    setReferralInfo,
    partnerInfo,
    setPartnerInfo,
    router,
  ]);

  const handleAcceptReferralInvite = React.useCallback(() => {
    setReferralIntroDismissed(true);
    // Land referral users on the search input. Effect 1 of
    // useOrgOnboardingUrlSync may run before referralInfo resolves async, so we
    // set it explicitly here too.
    setActiveUI('select_restaurant_from_google');
  }, [setActiveUI]);

  // Show intro screen (full-screen overlay) for referral, ambassador, or campaign
  if (shouldShowReferralIntro) {
    return (
      <React.Suspense fallback={null}>
        <ReferralIntroScreen
          variant={isCampaignPartner ? 'campaign' : 'referral'}
          campaignCode={isCampaignPartner ? partnerInfo?.partner_code : undefined}
          referrerRestaurantName={introName ?? ''}
          onAcceptInvite={handleAcceptReferralInvite}
          freeDays={introFreeDays}
        />
      </React.Suspense>
    );
  }

  return (
    <LayoutWrapper hideNavigation title="Onboarding" hasNavbar={false} hasFooter={false}>
      <OrganizationOnboardingHeader />

      {/* Resume Onboarding Modal */}
      <ResumeOnboardingModal
        isOpen={showResumeModal}
        onContinue={handleContinueOnboarding}
        onStartFresh={handleStartFresh}
      />

      {/* Restaurant and JP steps have their own layout/spacing, so just provide background */}
      {(currentStep === 'restaurant_creation' || JP_LIKE_STEPS.includes(currentStep)) && (
        <div className="min-h-screen bg-neutral-10">
          <OrgStepContainer stepKey="restaurant_creation">
            <RestaurantCreationStep />
          </OrgStepContainer>
          <OrgStepContainer stepKey="job_position_creation">
            <JobPositionCreationStep />
          </OrgStepContainer>
        </div>
      )}

      {/* Steps with ContentWrapper layout */}
      {currentStep === 'user_contact_details' && (
        <ContentWrapper>
          <OrgStepContainer stepKey="user_contact_details">
            <UserContactDetailsStep />
          </OrgStepContainer>
        </ContentWrapper>
      )}
    </LayoutWrapper>
  );
};

export default OrganizationOnboarding;
